import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import BackdropLoadingScreen from "../../../common/BackdropLoadingScreen";
import { createApi } from "../../../utils/createApi";
import { addIgnoreLabel } from "./SiteAboutPageUtils/addIgnoreLabel";
import {
  AutoPatrolFlexIgnoreZonesPreset,
  AutoPatrolSchedule,
  useSiteAboutStore,
} from "./siteAboutStore/siteAboutStore";
import { IgnoreZone } from "./SiteAboutPageUtils/siteAboutInterfaces";
import { FlexIgnoreZonesCameraPresetCard } from "./FlexIgnoreZonesOnboarding/FlexIgnoreZonesCameraPresetCard";
import { FlexIgnoreZonesEditPresetCard } from "./FlexIgnoreZonesOnboarding/FlexIgnoreZonesEditPresetCard";
import { PresetEditorStep } from "./FlexIgnoreZonesOnboarding/PresetEditorStep";
import { collapseNoIgnoreZonesPresetOptions } from "./FlexIgnoreZonesOnboarding/flexPresetDisplayDedupe";
import { SummaryStep } from "./FlexIgnoreZonesOnboarding/SummaryStep";
import { MetricSimplifiedRow } from "./SiteAboutPageUtils/bulkUpdateCamerasApi";
import { buildPresetZonePayload } from "./FlexIgnoreZonesOnboarding/flexIgnoreZonesOnboardingPayload";
import { deletePersistedFlexPresetRecordsAfterIgnoreZonesSave } from "./FlexIgnoreZonesOnboarding/flexIgnoreZonesPresetCleanup";
import {
  buildNoIgnoreZonesPostPayload,
  ignoreZoneStreamNameForCameraApi,
} from "./FlexIgnoreZonesOnboarding/flexIgnoreZonesOffboardingPayload";

export const AP_FLEX_NO_IGNORE_ZONES_OPTION = "ap-no-ignore-zones";
const AP_FLEX_CREATE_NEW_PRESET_OPTION = "ap-create-new-preset";

/** Stable empty list for `presetsByCamera[cameraId] ?? …` (avoids new `[]` each render). */
const EMPTY_AUTOPATROL_FLEX_PRESETS: AutoPatrolFlexIgnoreZonesPreset[] = [];

// Backend handoff notes (AutoPatrol-specific flex ignore zones endpoints):
// - GET /autopatrol/flex-ignore-zones/config?site_id={siteId}
// - GET /autopatrol/flex-ignore-zones/presets?camera_id={cameraId}
// - POST /autopatrol/flex-ignore-zones/presets
// - PUT /autopatrol/flex-ignore-zones/assignments
// - DELETE /autopatrol/flex-ignore-zones/assignments

export type AutoPatrolFlexCameraSummary = {
  id: number;
  camera_name: string;
  preview_url?: string;
  ignore_zones?: IgnoreZone[];
};

type LocalPresetDialogState = {
  scheduleId: number;
  cameraId: number;
  cameraName: string;
  presetLocalId: string;
} | null;

type AutoPatrolLiveCameraData = {
  preview_url?: string;
  ignore_zones?: IgnoreZone[];
};

const toCadenceText = (schedule: AutoPatrolSchedule) => {
  if (schedule.cadence != null && schedule.cadenceUnit) {
    const cadenceText = `${schedule.cadence} ${schedule.cadenceUnit}`;
    if (schedule.checksPerDay != null) {
      return `${cadenceText} (${schedule.checksPerDay} checks/day)`;
    }
    return cadenceText;
  }
  return schedule.cronDescription || "--";
};

const getSiteCameraMapFromStorage = () => {
  try {
    const raw = JSON.parse(localStorage.getItem("siteCameras") || '{"results":[]}');
    const results = Array.isArray(raw?.results) ? raw.results : [];
    return new Map<number, any>(
      results
        .map((camera: any) => [Number(camera.id), camera] as const)
        .filter(([id]: readonly [number, any]) => Number.isFinite(id))
    );
  } catch {
    return new Map<number, any>();
  }
};

const toScheduleCameras = (
  schedule: AutoPatrolSchedule,
  siteCameraMap: Map<number, any>
): AutoPatrolFlexCameraSummary[] =>
  (schedule.devices || []).map((device) => {
    const fromSite = siteCameraMap.get(Number(device.id));
    return {
      id: Number(device.id),
      camera_name: fromSite?.camera_name || device.deviceName || `Camera ${device.id}`,
      preview_url: fromSite?.preview_url || "",
      ignore_zones: addIgnoreLabel(fromSite?.ignore_zones || []),
    };
  });

const ensureCameraPresets = (
  camera: AutoPatrolFlexCameraSummary,
  presetsByCamera: Record<number, AutoPatrolFlexIgnoreZonesPreset[]>,
  setPresetsForCamera: (
    cameraId: number,
    presets: AutoPatrolFlexIgnoreZonesPreset[]
  ) => void
) => {
  const cameraZones = addIgnoreLabel(camera.ignore_zones || []);
  const existing = presetsByCamera[camera.id] || [];
  if (existing.length > 0) {
    const defaultPresetId = `default-${camera.id}`;
    const defaultPresetIndex = existing.findIndex(
      (preset) => preset.localId === defaultPresetId
    );
    if (defaultPresetIndex >= 0) {
      const defaultPreset = existing[defaultPresetIndex];
      const hasNewZones =
        cameraZones.length > 0 && (defaultPreset.zones || []).length === 0;
      if (hasNewZones) {
        const next = [...existing];
        next[defaultPresetIndex] = {
          ...defaultPreset,
          zones: cameraZones,
        };
        setPresetsForCamera(camera.id, next);
      }
    }
    return;
  }
  const defaultPreset: AutoPatrolFlexIgnoreZonesPreset = {
    localId: `default-${camera.id}`,
    cameraId: camera.id,
    name: "Default Preset",
    zones: cameraZones,
    persistedId: null,
    source: "default",
    userNamed: false,
  };
  const noIgnoreZonesPreset: AutoPatrolFlexIgnoreZonesPreset = {
    localId: AP_FLEX_NO_IGNORE_ZONES_OPTION,
    cameraId: camera.id,
    name: "No Ignore Zones",
    zones: [],
    persistedId: null,
    source: "custom",
    userNamed: true,
  };
  setPresetsForCamera(camera.id, [defaultPreset, noIgnoreZonesPreset]);
};

const buildCameraContext = (camera: AutoPatrolFlexCameraSummary) => ({
  loaded: true,
  loading: false,
  error: null,
  preview: camera.preview_url
    ? {
        previewUrl: camera.preview_url,
        adjustedDimensions: { x: 720, y: 405 },
      }
    : null,
  defaultZones: addIgnoreLabel(camera.ignore_zones || []),
});

const replaceNegativeWithZero = (data: number[][][]): number[][][] =>
  data.map((polygon) =>
    polygon.map((coordinates) => coordinates.map((num) => (num < 0 ? 0 : num)))
  );

const buildNextPresetName = (
  cameraId: number,
  presetsByCamera: Record<number, AutoPatrolFlexIgnoreZonesPreset[]>
) => {
  const presets = presetsByCamera[cameraId] || [];
  let index = 2;
  while (presets.some((preset) => preset.name === `Preset ${index}`)) {
    index += 1;
  }
  return `Preset ${index}`;
};

const parseListResponse = (data: unknown): any[] => {
  if (data && typeof data === "object" && Array.isArray((data as any).results)) {
    return (data as any).results;
  }
  if (Array.isArray(data)) {
    return data;
  }
  return [];
};

const toNumericId = (value: unknown): number | null => {
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
};

const coerceCoordinatesToPolygonList = (raw: unknown): number[][][] => {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [];
  }
  const first = raw[0];
  if (!Array.isArray(first)) {
    return [];
  }
  const firstFirst = first[0];
  if (typeof firstFirst === "number") {
    return [raw as number[][]];
  }
  if (Array.isArray(firstFirst) && typeof firstFirst[0] === "number") {
    return raw as number[][][];
  }
  return [];
};

const normalizeZoneName = (value: string) =>
  value.trim().toLowerCase().replace(/^ignore[_\s]+/, "").replace(/\s+/g, " ");

const presetZoneLabelDisplayName = (zone: { name?: string }) => {
  const raw = (zone.name || "").trim();
  if (!raw) {
    return "Preset zone";
  }
  return raw.replace(/\s*#\d+\s*$/u, "").trim() || raw;
};

const pickDefaultZoneTemplate = (
  defaultZones: IgnoreZone[],
  candidateName: string,
  metricId?: number
) => {
  const byMetric =
    typeof metricId === "number" && Number.isFinite(metricId)
      ? defaultZones.find((zone) => Number(zone.metric_id) === metricId)
      : undefined;
  if (byMetric) {
    return byMetric;
  }
  const normalizedCandidate = normalizeZoneName(candidateName || "");
  if (!normalizedCandidate) {
    return undefined;
  }
  return defaultZones.find((zone) => {
    const displayName = normalizeZoneName(zone.label_display_name || "");
    const labelName = normalizeZoneName(zone.label || "");
    return displayName === normalizedCandidate || labelName === normalizedCandidate;
  });
};

const pickIgnoreAllTemplate = (defaultZones: IgnoreZone[]): IgnoreZone | undefined =>
  defaultZones.find((zone) => Number(zone.metric_id) === 0) ||
  defaultZones.find((zone) => Number(zone.zone_type) === 0) ||
  defaultZones.find((zone) => normalizeZoneName(zone.label || "") === "all") ||
  defaultZones.find(
    (zone) => normalizeZoneName(zone.label_display_name || "") === "all"
  ) ||
  defaultZones.find((zone) => normalizeZoneName(zone.label || "") === "motion") ||
  defaultZones.find(
    (zone) => normalizeZoneName(zone.label_display_name || "") === "motion"
  );

const polygonSignature = (polygon: number[][]) => JSON.stringify(polygon || []);

const toMappedPresetZones = (
  presetName: string,
  presetZonesRaw: any[],
  defaultZones: IgnoreZone[]
): IgnoreZone[] =>
  presetZonesRaw
    .map((zone: any, zoneIndex: number) => {
      const labels = Array.isArray(zone?.labels)
        ? zone.labels.filter((labelEntry: any) => labelEntry && typeof labelEntry === "object")
        : [];
      if (labels.length > 0) {
        const nestedLabelCoordinateLists = labels.map((labelEntry: any) =>
          coerceCoordinatesToPolygonList(labelEntry.coordinates ?? zone.coordinates)
        );
        const coordinateSignatures = labels.map((labelEntry: any) =>
          JSON.stringify(
            coerceCoordinatesToPolygonList(labelEntry.coordinates ?? zone.coordinates)
          )
        );
        const uniqueCoordinateSignatures = new Set(coordinateSignatures);
        const polygonFrequency = new Map<string, number>();
        const polygonBySignature = new Map<string, number[][]>();
        nestedLabelCoordinateLists.forEach((polygonList: number[][][]) => {
          const uniqueInLabel = new Set<string>();
          polygonList.forEach((polygon: number[][]) => {
            const sig = polygonSignature(polygon);
            polygonBySignature.set(sig, polygon);
            uniqueInLabel.add(sig);
          });
          uniqueInLabel.forEach((sig) => {
            polygonFrequency.set(sig, (polygonFrequency.get(sig) || 0) + 1);
          });
        });
        const sharedPolygonThreshold = Math.max(2, Math.ceil(labels.length / 2));
        const sharedPolygonSignatures = Array.from(polygonFrequency.entries())
          .filter(([, count]) => count >= sharedPolygonThreshold)
          .map(([sig]) => sig);
        const sharedPolygons = sharedPolygonSignatures
          .map((sig) => polygonBySignature.get(sig))
          .filter((polygon): polygon is number[][] => Array.isArray(polygon));

        let nestedMappedZones = labels
          .map((labelEntry: any, labelIndex: number) => {
            const labelName = String(
              labelEntry.name || labelEntry.label_name || zone.name || "preset_zone"
            );
            const labelMetricId = Number(
              labelEntry.label ?? labelEntry.label_id ?? zone.metric_id ?? 0
            );
            const baseTemplate = pickDefaultZoneTemplate(
              defaultZones,
              labelName,
              Number.isFinite(labelMetricId) ? labelMetricId : undefined
            );
            const presetZoneId = toNumericId(zone.id);
            const resolvedLineColor =
              zone.line_color || baseTemplate?.line_color || "#FF8400";
            return {
              zone_type:
                zone.zone_type ??
                baseTemplate?.zone_type ??
                (Number.isFinite(labelMetricId) && labelMetricId > 0 ? 1 : 0),
              stream_id: zone.stream_id ?? baseTemplate?.stream_id ?? 0,
              stream_name: zone.stream_name ?? baseTemplate?.stream_name ?? "",
              metric_id: Number.isFinite(labelMetricId)
                ? labelMetricId
                : baseTemplate?.metric_id || 0,
              label: `${labelName}-${zoneIndex}-${labelIndex}`,
              label_display_name:
                baseTemplate?.label_display_name ||
                presetZoneLabelDisplayName({ name: labelName }),
              line_color: resolvedLineColor,
              coordinates: coerceCoordinatesToPolygonList(
                labelEntry.coordinates ?? zone.coordinates
              ) as unknown as IgnoreZone["coordinates"],
              ...(presetZoneId != null ? { preset_zone_id: presetZoneId } : {}),
            } as IgnoreZone;
          })
          .filter((zoneEntry: IgnoreZone) => zoneEntry.coordinates.length > 0);

        if (
          uniqueCoordinateSignatures.size > 1 &&
          sharedPolygons.length > 0 &&
          nestedMappedZones.length > 0
        ) {
          const ignoreAllTemplate = pickIgnoreAllTemplate(defaultZones);
          const sharedPolygonSignatureSet = new Set(
            sharedPolygons.map((polygon) => polygonSignature(polygon))
          );
          const hasIgnoreAll = nestedMappedZones.some(
            (zoneEntry: IgnoreZone) => Number(zoneEntry.zone_type) === 0
          );
          if (!hasIgnoreAll) {
            nestedMappedZones.unshift({
              zone_type: 0,
              stream_id: zone.stream_id ?? ignoreAllTemplate?.stream_id ?? 0,
              stream_name: zone.stream_name ?? ignoreAllTemplate?.stream_name ?? "",
              metric_id: ignoreAllTemplate?.metric_id ?? 0,
              label: "ignore_all_inferred",
              label_display_name: ignoreAllTemplate?.label_display_name || "Ignore All",
              line_color: ignoreAllTemplate?.line_color || "#ffff00",
              coordinates: sharedPolygons as unknown as IgnoreZone["coordinates"],
              ...(zone.id != null && Number.isFinite(Number(zone.id))
                ? { preset_zone_id: Number(zone.id) }
                : {}),
            });
          }
          nestedMappedZones = nestedMappedZones
            .map((zoneEntry: IgnoreZone) => {
              if (Number(zoneEntry.zone_type) === 0) {
                return {
                  ...zoneEntry,
                  zone_type: 0,
                  metric_id: ignoreAllTemplate?.metric_id ?? zoneEntry.metric_id,
                  label: "ignore_all_inferred",
                  label_display_name:
                    ignoreAllTemplate?.label_display_name || "Ignore All",
                  line_color:
                    ignoreAllTemplate?.line_color || zoneEntry.line_color || "#ffff00",
                  coordinates:
                    sharedPolygons as unknown as IgnoreZone["coordinates"],
                };
              }
              const filteredCoordinates = zoneEntry.coordinates.filter(
                (polygon: number[]) =>
                  !sharedPolygonSignatureSet.has(
                    polygonSignature(polygon as unknown as number[][])
                  )
              );
              return {
                ...zoneEntry,
                coordinates: filteredCoordinates,
              };
            })
            .filter((zoneEntry: IgnoreZone) => zoneEntry.coordinates.length > 0);
        }

        if (uniqueCoordinateSignatures.size === 1 && nestedMappedZones.length > 0) {
          const ignoreAllTemplate = pickIgnoreAllTemplate(defaultZones);
          const hasIgnoreAll = nestedMappedZones.some(
            (zoneEntry: IgnoreZone) => Number(zoneEntry.zone_type) === 0
          );
          const inferredCoordinates = coerceCoordinatesToPolygonList(
            labels[0]?.coordinates ?? zone.coordinates
          );
          if (!hasIgnoreAll) {
            nestedMappedZones.unshift({
              zone_type: 0,
              stream_id: zone.stream_id ?? ignoreAllTemplate?.stream_id ?? 0,
              stream_name: zone.stream_name ?? ignoreAllTemplate?.stream_name ?? "",
              metric_id: ignoreAllTemplate?.metric_id ?? 0,
              label: "ignore_all_inferred",
              label_display_name: ignoreAllTemplate?.label_display_name || "Ignore All",
              line_color: ignoreAllTemplate?.line_color || "#FF8400",
              coordinates: inferredCoordinates as unknown as IgnoreZone["coordinates"],
              ...(zone.id != null && Number.isFinite(Number(zone.id))
                ? { preset_zone_id: Number(zone.id) }
                : {}),
            });
          }
          const ignoreAllZone =
            nestedMappedZones.find(
              (zoneEntry: IgnoreZone) => Number(zoneEntry.zone_type) === 0
            ) || nestedMappedZones[0];
          nestedMappedZones = [
            {
              ...ignoreAllZone,
              zone_type: 0,
              label: "ignore_all_inferred",
              label_display_name:
                ignoreAllTemplate?.label_display_name || "Ignore All",
              line_color:
                ignoreAllZone.line_color || ignoreAllTemplate?.line_color || "#FF8400",
              coordinates: inferredCoordinates as unknown as IgnoreZone["coordinates"],
            },
          ];
        }

        return nestedMappedZones;
      }
      const metricId = toNumericId(zone.metric_id) ?? 0;
      const presetZoneId = toNumericId(zone.id);
      const baseName = zone.name || "preset_zone";
      return {
        zone_type: zone.zone_type || 0,
        stream_id: zone.stream_id || 0,
        stream_name: zone.stream_name || "",
        metric_id: metricId,
        label: `${baseName}-${zoneIndex}`,
        label_display_name: presetZoneLabelDisplayName(zone),
        line_color: zone.line_color || "#FF8400",
        coordinates: coerceCoordinatesToPolygonList(zone.coordinates) as unknown as IgnoreZone["coordinates"],
        ...(presetZoneId != null ? { preset_zone_id: presetZoneId } : {}),
      } as IgnoreZone;
    })
    .flat();

export const syncAutoPatrolFlexIgnoreZonesFromApi = async ({
  siteId,
  schedules,
  existingPresetsByCamera,
  setEnabledForSite,
  setPresetsForCamera,
  setSelectedPresetForScheduleCamera,
}: {
  siteId: number;
  schedules: AutoPatrolSchedule[];
  existingPresetsByCamera: Record<number, AutoPatrolFlexIgnoreZonesPreset[]>;
  setEnabledForSite: (siteId: number, enabled: boolean) => void;
  setPresetsForCamera: (
    cameraId: number,
    presets: AutoPatrolFlexIgnoreZonesPreset[]
  ) => void;
  setSelectedPresetForScheduleCamera: (
    scheduleId: number,
    cameraId: number,
    presetLocalId: string
  ) => void;
}) => {
  if (!siteId || schedules.length === 0) {
    return;
  }
  const AxiosUI = createApi("");
  const siteCameraMap = getSiteCameraMapFromStorage();
  const camerasByScheduleId: Record<number, AutoPatrolFlexCameraSummary[]> = {};
  schedules.forEach((schedule) => {
    camerasByScheduleId[schedule.id] = toScheduleCameras(schedule, siteCameraMap);
  });
  const cameraDefaultZonesById = new Map<number, IgnoreZone[]>();
  Object.values(camerasByScheduleId)
    .flat()
    .forEach((camera) => {
      if (!cameraDefaultZonesById.has(camera.id)) {
        cameraDefaultZonesById.set(camera.id, addIgnoreLabel(camera.ignore_zones || []));
      }
    });
  const uniqueCameraIds = Array.from(
    new Set(
      Object.values(camerasByScheduleId)
        .flat()
        .map((camera) => camera.id)
    )
  );
  if (uniqueCameraIds.length === 0) {
    setEnabledForSite(siteId, false);
    return;
  }

  try {
    const presetsByCameraFromApi = new Map<number, any[]>();
    const ignoreZonesByCameraFromApi = new Map<number, IgnoreZone[]>();
    await Promise.all(
      uniqueCameraIds.map(async (cameraId) => {
        try {
          const presetsResponse = await AxiosUI.get(`preset/?camera=${cameraId}`);
          presetsByCameraFromApi.set(cameraId, parseListResponse(presetsResponse.data));
        } catch {
          presetsByCameraFromApi.set(cameraId, []);
        }
        try {
          const ignoreZonesResponse = await AxiosUI.get(
            `camera/${cameraId}/ignore_zones/`
          );
          ignoreZonesByCameraFromApi.set(
            cameraId,
            addIgnoreLabel(parseListResponse(ignoreZonesResponse.data))
          );
        } catch {
          ignoreZonesByCameraFromApi.set(cameraId, []);
        }
      })
    );
    ignoreZonesByCameraFromApi.forEach((ignoreZones, cameraId) => {
      if (ignoreZones.length > 0) {
        cameraDefaultZonesById.set(cameraId, ignoreZones);
      }
    });

    const assignmentRowsByScheduleId = await Promise.all(
      schedules.map(async (schedule) => {
        try {
          const response = await AxiosUI.get(
            `auto-patrol-schedule-preset/?auto_patrol_schedule=${schedule.id}`
          );
          return [schedule.id, parseListResponse(response.data)] as const;
        } catch {
          return [schedule.id, [] as any[]] as const;
        }
      })
    );

    const hasAnyPresets = Array.from(presetsByCameraFromApi.values()).some(
      (presets) => presets.length > 0
    );
    const hasAnyAssignments = assignmentRowsByScheduleId.some(([, rows]) => rows.length > 0);
    setEnabledForSite(siteId, hasAnyPresets || hasAnyAssignments);

    const nextPresetsByCamera: Record<number, AutoPatrolFlexIgnoreZonesPreset[]> = {};
    uniqueCameraIds.forEach((cameraId) => {
      nextPresetsByCamera[cameraId] = [...(existingPresetsByCamera[cameraId] || [])];
    });

    const presetIdToCameraId = new Map<number, number>();
    presetsByCameraFromApi.forEach((presets, cameraId) => {
      presets.forEach((presetRow) => {
        const presetId = toNumericId((presetRow as any)?.id);
        if (presetId != null) {
          presetIdToCameraId.set(presetId, cameraId);
        }
      });
    });

    await Promise.all(
      uniqueCameraIds.map(async (cameraId) => {
        const cameraPresets = nextPresetsByCamera[cameraId];
        const defaults = cameraPresets.find((preset) => preset.localId === `default-${cameraId}`);
        const defaultZonesFromPreset = addIgnoreLabel(defaults?.zones || []);
        const defaultZonesFromCameraState = cameraDefaultZonesById.get(cameraId) || [];
        const defaultZones =
          defaultZonesFromPreset.length > 0
            ? defaultZonesFromPreset
            : defaultZonesFromCameraState;
        const cameraApiPresets = presetsByCameraFromApi.get(cameraId) || [];
        await Promise.all(
          cameraApiPresets.map(async (presetRow) => {
            const presetId = toNumericId((presetRow as any)?.id);
            if (presetId == null) {
              return;
            }
            const localId = `server-${presetId}`;
            const exists = cameraPresets.some(
              (preset) => preset.localId === localId || preset.persistedId === presetId
            );
            if (exists) {
              return;
            }
            const presetName = String((presetRow as any)?.name || `Preset ${presetId}`);
            let mappedZones: IgnoreZone[] = [];
            try {
              const presetZoneResponse = await AxiosUI.get(
                `preset-zone/?camera=${cameraId}&preset=${presetId}`
              );
              mappedZones = toMappedPresetZones(
                presetName,
                parseListResponse(presetZoneResponse.data),
                defaultZones
              );
            } catch {
              mappedZones = [];
            }
            cameraPresets.push({
              localId,
              cameraId,
              name: presetName,
              zones: addIgnoreLabel(mappedZones),
              persistedId: presetId,
              source: "custom",
              userNamed: true,
            });
          })
        );
      })
    );

    Object.entries(nextPresetsByCamera).forEach(([cameraId, presets]) => {
      if (presets.length > 0) {
        setPresetsForCamera(Number(cameraId), presets);
      }
    });

    assignmentRowsByScheduleId.forEach(([scheduleId, rows]) => {
      rows.forEach((row: any) => {
        const presetId = toNumericId(row?.preset?.id ?? row?.preset);
        if (presetId == null) {
          return;
        }
        const cameraId =
          toNumericId(row?.camera?.id ?? row?.camera) ?? presetIdToCameraId.get(presetId);
        if (cameraId == null) {
          // TODO(api): confirm expected mapping source when assignment rows omit camera ids.
          return;
        }
        const localId = `server-${presetId}`;
        const presets = nextPresetsByCamera[cameraId] || [];
        const hasPreset = presets.some(
          (preset) => preset.localId === localId || preset.persistedId === presetId
        );
        setSelectedPresetForScheduleCamera(
          scheduleId,
          cameraId,
          hasPreset ? localId : `default-${cameraId}`
        );
      });
    });
  } catch {
    // Leave current state unchanged when sync fails.
  }
};

export const AutoPatrolFlexIgnoreZonesOnboardingDialog = ({
  siteId,
  schedules,
  open,
  onClose,
}: {
  siteId: number;
  schedules: AutoPatrolSchedule[];
  open: boolean;
  onClose: () => void;
}) => {
  const {
    autoPatrolFlexIgnoreZonesActiveScheduleIndexBySite,
    setAutoPatrolFlexIgnoreZonesActiveScheduleIndexForSite,
    autoPatrolFlexIgnoreZonesPresetsByCamera,
    setAutoPatrolFlexIgnoreZonesPresetsForCamera,
    autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera,
    setAutoPatrolFlexIgnoreZonesSelectedPresetForScheduleCamera,
    initializeAutoPatrolFlexIgnoreZonesForSite,
    setScheduleChangesMade,
  } = useSiteAboutStore();

  const [newPresetDialog, setNewPresetDialog] =
    React.useState<LocalPresetDialogState>(null);
  const [editorPresetName, setEditorPresetName] = React.useState("");
  const [wizardStep, setWizardStep] = React.useState<"scheduleSelection" | "summary">(
    "scheduleSelection"
  );
  const [liveCameraDataById, setLiveCameraDataById] = React.useState<
    Record<number, AutoPatrolLiveCameraData>
  >({});
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const inFlightCameraIdsRef = React.useRef<Set<number>>(new Set());
  const initializedConfigKeyRef = React.useRef<string>("");
  const assignmentsHydratedKeyRef = React.useRef<string>("");
  const assignmentsHydrationInFlightRef = React.useRef(false);

  const apSchedules = React.useMemo(
    () => schedules.filter((schedule) => schedule.patrolType === "AutoPatrol"),
    [schedules]
  );
  const siteCameraMap = React.useMemo(() => getSiteCameraMapFromStorage(), [open]);
  const camerasByScheduleId = React.useMemo(() => {
    const map: Record<number, AutoPatrolFlexCameraSummary[]> = {};
    apSchedules.forEach((schedule) => {
      map[schedule.id] = toScheduleCameras(schedule, siteCameraMap).map((camera) => {
        const live = liveCameraDataById[camera.id];
        return {
          ...camera,
          preview_url: live?.preview_url || camera.preview_url || "",
          ignore_zones: addIgnoreLabel(live?.ignore_zones || camera.ignore_zones || []),
        };
      });
    });
    return map;
  }, [apSchedules, siteCameraMap, liveCameraDataById]);
  const dialogConfigKey = React.useMemo(
    () =>
      JSON.stringify(
        apSchedules.map((schedule) => ({
          scheduleId: schedule.id,
          cameraIds: (camerasByScheduleId[schedule.id] || []).map((camera) => camera.id),
        }))
      ),
    [apSchedules, camerasByScheduleId]
  );

  React.useEffect(() => {
    if (!open) {
      initializedConfigKeyRef.current = "";
      setWizardStep((previous) =>
        previous === "scheduleSelection" ? previous : "scheduleSelection"
      );
      return;
    }
    if (initializedConfigKeyRef.current === dialogConfigKey) {
      return;
    }
    const scheduleIds = apSchedules.map((schedule) => schedule.id);
    const cameraIdsBySchedule = Object.fromEntries(
      apSchedules.map((schedule) => [
        schedule.id,
        (camerasByScheduleId[schedule.id] || []).map((camera) => camera.id),
      ])
    );
    initializeAutoPatrolFlexIgnoreZonesForSite({
      siteId,
      scheduleIds,
      cameraIdsBySchedule,
    });
    setAutoPatrolFlexIgnoreZonesActiveScheduleIndexForSite(siteId, 0);
    setWizardStep("scheduleSelection");
    initializedConfigKeyRef.current = dialogConfigKey;
  }, [
    open,
    dialogConfigKey,
    apSchedules,
    camerasByScheduleId,
    siteId,
    initializeAutoPatrolFlexIgnoreZonesForSite,
    setAutoPatrolFlexIgnoreZonesActiveScheduleIndexForSite,
  ]);

  React.useEffect(() => {
    if (!open) {
      setLiveCameraDataById((previous) =>
        Object.keys(previous).length === 0 ? previous : {}
      );
      assignmentsHydratedKeyRef.current = "";
      assignmentsHydrationInFlightRef.current = false;
      if (inFlightCameraIdsRef.current.size > 0) {
        inFlightCameraIdsRef.current = new Set();
      }
      return;
    }
    const AxiosUI = createApi("");
    const uniqueCameraIds = Array.from(
      new Set(
        Object.values(camerasByScheduleId)
          .flat()
          .map((camera) => camera.id)
      )
    );
    const toFetch = uniqueCameraIds.filter((cameraId) => {
      const alreadyLoaded = liveCameraDataById[cameraId] != null;
      const inFlight = inFlightCameraIdsRef.current.has(cameraId);
      return !alreadyLoaded && !inFlight;
    });
    if (toFetch.length === 0) {
      return;
    }
    toFetch.forEach((cameraId) => inFlightCameraIdsRef.current.add(cameraId));
    void Promise.all(
      toFetch.map(async (cameraId) => {
        try {
          const [statusResponse, zonesResponse] = await Promise.all([
            AxiosUI.get(`/camera/${cameraId}/status/`, { timeout: 8000 }),
            AxiosUI.get(`/camera/${cameraId}/ignore_zones/`, { timeout: 8000 }),
          ]);
          setLiveCameraDataById((previous) => ({
            ...previous,
            [cameraId]: {
              preview_url: String(statusResponse.data?.preview_url || "").trim(),
              ignore_zones: addIgnoreLabel(zonesResponse.data || []),
            },
          }));
        } catch (error: any) {
          // Keep fallback data from cached site cameras when live endpoints fail.
        }
        inFlightCameraIdsRef.current.delete(cameraId);
      })
    );
  }, [open, camerasByScheduleId, liveCameraDataById]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    Object.values(camerasByScheduleId)
      .flat()
      .forEach((camera) => {
        ensureCameraPresets(
          camera,
          autoPatrolFlexIgnoreZonesPresetsByCamera,
          setAutoPatrolFlexIgnoreZonesPresetsForCamera
        );
      });
  }, [
    open,
    camerasByScheduleId,
    autoPatrolFlexIgnoreZonesPresetsByCamera,
    setAutoPatrolFlexIgnoreZonesPresetsForCamera,
  ]);

  React.useEffect(() => {
    if (!open || apSchedules.length === 0) {
      return;
    }
    const hydrationKey = `${siteId}:${apSchedules.map((schedule) => schedule.id).join(",")}`;
    if (
      assignmentsHydratedKeyRef.current === hydrationKey ||
      assignmentsHydrationInFlightRef.current
    ) {
      return;
    }
    assignmentsHydrationInFlightRef.current = true;
    const AxiosUI = createApi("");

    void (async () => {
      try {
        const uniqueCameraIds = Array.from(
          new Set(
            apSchedules.flatMap((schedule) =>
              (camerasByScheduleId[schedule.id] || []).map((camera) => camera.id)
            )
          )
        );
        const presetsByCameraFromApi = new Map<number, any[]>();
        await Promise.all(
          uniqueCameraIds.map(async (cameraId) => {
            try {
              const presetsResponse = await AxiosUI.get(`preset/?camera=${cameraId}`);
              presetsByCameraFromApi.set(cameraId, parseListResponse(presetsResponse.data));
            } catch {
              presetsByCameraFromApi.set(cameraId, []);
            }
          })
        );

        const presetIdToCameraId = new Map<number, number>();
        presetsByCameraFromApi.forEach((presets, cameraId) => {
          presets.forEach((presetRow) => {
            const presetId = toNumericId((presetRow as any)?.id);
            if (presetId != null) {
              presetIdToCameraId.set(presetId, cameraId);
            }
          });
        });

        const nextPresetsByCamera: Record<number, AutoPatrolFlexIgnoreZonesPreset[]> = {};
        uniqueCameraIds.forEach((cameraId) => {
          nextPresetsByCamera[cameraId] = [
            ...(autoPatrolFlexIgnoreZonesPresetsByCamera[cameraId] || []),
          ];
        });

        const assignmentRowsByScheduleId = await Promise.all(
          apSchedules.map(async (schedule) => {
            try {
              const response = await AxiosUI.get(
                `auto-patrol-schedule-preset/?auto_patrol_schedule=${schedule.id}`
              );
              return [schedule.id, parseListResponse(response.data)] as const;
            } catch {
              return [schedule.id, [] as any[]] as const;
            }
          })
        );

        assignmentRowsByScheduleId.forEach(([scheduleId, rows]) => {
          rows.forEach((row: any) => {
            const presetId = toNumericId(row?.preset?.id ?? row?.preset);
            if (presetId == null) {
              return;
            }
            const cameraIdFromAssignment = toNumericId(row?.camera?.id ?? row?.camera);
            const cameraId = cameraIdFromAssignment ?? presetIdToCameraId.get(presetId);
            if (cameraId == null) {
              return;
            }
            const existingForCamera = nextPresetsByCamera[cameraId] || [];
            const localId = `server-${presetId}`;
            const hasPreset = existingForCamera.some(
              (preset) => preset.localId === localId || preset.persistedId === presetId
            );
            if (!hasPreset) {
              const presetMeta = (presetsByCameraFromApi.get(cameraId) || []).find(
                (presetRow) => toNumericId((presetRow as any)?.id) === presetId
              ) as any;
              const defaultPreset = existingForCamera.find(
                (preset) => preset.localId === `default-${cameraId}`
              );
              const defaultZones = addIgnoreLabel(defaultPreset?.zones || []);
              const appendPreset = (zones: IgnoreZone[]) => {
                existingForCamera.push({
                  localId,
                  cameraId,
                  name: String(presetMeta?.name || `Preset ${presetId}`),
                  zones: addIgnoreLabel(zones),
                  persistedId: presetId,
                  source: "custom",
                  userNamed: true,
                });
              };
              void AxiosUI.get(`preset-zone/?camera=${cameraId}&preset=${presetId}`)
                .then((presetZoneResponse) => {
                  appendPreset(
                    toMappedPresetZones(
                      String(presetMeta?.name || `Preset ${presetId}`),
                      parseListResponse(presetZoneResponse.data),
                      defaultZones
                    )
                  );
                  setAutoPatrolFlexIgnoreZonesPresetsForCamera(cameraId, [
                    ...existingForCamera,
                  ]);
                })
                .catch(() => {
                  appendPreset([]);
                  setAutoPatrolFlexIgnoreZonesPresetsForCamera(cameraId, [
                    ...existingForCamera,
                  ]);
                });
            }
            setAutoPatrolFlexIgnoreZonesSelectedPresetForScheduleCamera(
              scheduleId,
              cameraId,
              localId
            );
          });
        });

        Object.entries(nextPresetsByCamera).forEach(([cameraId, presets]) => {
          if (presets.length > 0) {
            setAutoPatrolFlexIgnoreZonesPresetsForCamera(Number(cameraId), presets);
          }
        });
        assignmentsHydratedKeyRef.current = hydrationKey;
      } finally {
        assignmentsHydrationInFlightRef.current = false;
      }
    })();
  }, [
    open,
    siteId,
    apSchedules,
    camerasByScheduleId,
    autoPatrolFlexIgnoreZonesPresetsByCamera,
    setAutoPatrolFlexIgnoreZonesPresetsForCamera,
    setAutoPatrolFlexIgnoreZonesSelectedPresetForScheduleCamera,
  ]);

  const activeScheduleIndex = Math.min(
    autoPatrolFlexIgnoreZonesActiveScheduleIndexBySite[siteId] || 0,
    Math.max(apSchedules.length - 1, 0)
  );
  const currentSchedule = apSchedules[activeScheduleIndex];
  const currentScheduleCameras = currentSchedule
    ? camerasByScheduleId[currentSchedule.id] || []
    : [];

  const handleSubmit = async () => {
    const AxiosUI = createApi("");
    setSubmitLoading(true);
    try {
      type SubmitCell = {
        scheduleId: number;
        cameraId: number;
        presetLocalId: string;
        preset: AutoPatrolFlexIgnoreZonesPreset;
      };

      const submitCells: SubmitCell[] = [];
      apSchedules.forEach((schedule) => {
        const cameras = camerasByScheduleId[schedule.id] || [];
        cameras.forEach((camera) => {
          const presetLocalId =
            autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera[
              `${schedule.id}:${camera.id}`
            ] || `default-${camera.id}`;
          const preset = (autoPatrolFlexIgnoreZonesPresetsByCamera[camera.id] || []).find(
            (item) => item.localId === presetLocalId
          );
          if (!preset) {
            return;
          }
          submitCells.push({
            scheduleId: schedule.id,
            cameraId: camera.id,
            presetLocalId,
            preset,
          });
        });
      });

      if (submitCells.length === 0) {
        setScheduleChangesMade(true);
        onClose();
        return;
      }

      const uniqueByCameraPreset = new Map<
        string,
        { cameraId: number; presetLocalId: string; preset: AutoPatrolFlexIgnoreZonesPreset }
      >();
      submitCells.forEach((cell) => {
        const key = `${cell.cameraId}:${cell.presetLocalId}`;
        if (!uniqueByCameraPreset.has(key)) {
          uniqueByCameraPreset.set(key, {
            cameraId: cell.cameraId,
            presetLocalId: cell.presetLocalId,
            preset: cell.preset,
          });
        }
      });

      let metricRows: MetricSimplifiedRow[] = [];
      const needsMetricRows = Array.from(uniqueByCameraPreset.values()).some(
        (entry) => entry.preset.persistedId == null && (entry.preset.zones || []).length > 0
      );
      if (needsMetricRows) {
        const siteStore = useSiteAboutStore.getState();
        if (
          siteStore.siteMetricsSimplifiedCustomerId === siteId &&
          siteStore.siteMetricsSimplified.length > 0
        ) {
          metricRows = siteStore.siteMetricsSimplified;
        } else {
          const metricResponse = await AxiosUI.get("metric/simplified/", {
            params: { customer: siteId },
          });
          metricRows = parseListResponse(metricResponse.data) as MetricSimplifiedRow[];
          siteStore.setSiteMetricsSimplifiedData(metricRows, siteId);
        }
      }

      const persistedIdByCameraPreset = new Map<string, number>();
      const nextPresetsByCamera: Record<number, AutoPatrolFlexIgnoreZonesPreset[]> = {};
      Object.keys(autoPatrolFlexIgnoreZonesPresetsByCamera).forEach((cameraId) => {
        nextPresetsByCamera[Number(cameraId)] = [
          ...autoPatrolFlexIgnoreZonesPresetsByCamera[Number(cameraId)],
        ];
      });

      for (const [cameraPresetKey, meta] of uniqueByCameraPreset.entries()) {
        const presetName = (meta.preset.name || "").trim() || "Preset";
        if (meta.preset.persistedId != null && Number.isFinite(Number(meta.preset.persistedId))) {
          persistedIdByCameraPreset.set(cameraPresetKey, Number(meta.preset.persistedId));
          continue;
        }
        const createdPresetResponse = await AxiosUI.post("preset/", {
          camera: meta.cameraId,
          name: presetName,
        });
        const createdPresetId = toNumericId(createdPresetResponse.data?.id);
        if (createdPresetId == null) {
          throw new Error(`Unable to create preset for camera ${meta.cameraId}.`);
        }
        if ((meta.preset.zones || []).length > 0) {
          const presetZonePayload = buildPresetZonePayload({
            cameraId: meta.cameraId,
            presetId: createdPresetId,
            presetName: `${presetName} #${createdPresetId}`,
            zones: meta.preset.zones || [],
            metricRows,
          });
          await AxiosUI.post("preset-zone/", presetZonePayload);
        }
        persistedIdByCameraPreset.set(cameraPresetKey, createdPresetId);
        const current = nextPresetsByCamera[meta.cameraId] || [];
        nextPresetsByCamera[meta.cameraId] = current.map((preset) =>
          preset.localId === meta.presetLocalId
            ? { ...preset, persistedId: createdPresetId, name: presetName }
            : preset
        );
      }

      Object.entries(nextPresetsByCamera).forEach(([cameraId, presets]) => {
        setAutoPatrolFlexIgnoreZonesPresetsForCamera(Number(cameraId), presets);
      });

      const existingAssignmentByScheduleCamera = new Map<string, number>();
      const persistedPresetIdToCameraId = new Map<number, number>();
      Object.entries(nextPresetsByCamera).forEach(([cameraId, presets]) => {
        presets.forEach((preset) => {
          if (preset.persistedId != null && Number.isFinite(Number(preset.persistedId))) {
            persistedPresetIdToCameraId.set(Number(preset.persistedId), Number(cameraId));
          }
        });
      });

      const assignmentRowsByScheduleId = await Promise.all(
        apSchedules.map(async (schedule) => {
          try {
            const response = await AxiosUI.get(
              `auto-patrol-schedule-preset/?auto_patrol_schedule=${schedule.id}`
            );
            return [schedule.id, parseListResponse(response.data)] as const;
          } catch {
            return [schedule.id, [] as any[]] as const;
          }
        })
      );
      assignmentRowsByScheduleId.forEach(([scheduleId, rows]) => {
        rows.forEach((row: any) => {
          const presetId = toNumericId(row?.preset?.id ?? row?.preset);
          if (presetId == null) {
            return;
          }
          const cameraId =
            toNumericId(row?.camera?.id ?? row?.camera) ??
            persistedPresetIdToCameraId.get(presetId);
          if (cameraId == null) {
            return;
          }
          existingAssignmentByScheduleCamera.set(`${scheduleId}:${cameraId}`, presetId);
        });
      });

      for (const cell of submitCells) {
        const desiredPresetId = persistedIdByCameraPreset.get(
          `${cell.cameraId}:${cell.presetLocalId}`
        );
        if (desiredPresetId == null) {
          continue;
        }
        const existingPresetId = existingAssignmentByScheduleCamera.get(
          `${cell.scheduleId}:${cell.cameraId}`
        );
        if (existingPresetId === desiredPresetId) {
          continue;
        }
        // TODO(api): confirm whether backend expects stale assignments to be explicitly deleted.
        await AxiosUI.post("auto-patrol-schedule-preset/", {
          auto_patrol_schedule: cell.scheduleId,
          preset: desiredPresetId,
        });
      }

      setScheduleChangesMade(true);
      setSubmitLoading(false);
      onClose();
    } catch (error: any) {
      setSubmitLoading(false);
      alert(
        error?.message ||
          "Unable to save AutoPatrol Flex Ignore Zones. Please try again."
      );
    }
  };

  const assignments = React.useMemo(() => {
    return Object.entries(autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera)
      .map(([key, presetLocalId]) => {
        const [scheduleId, cameraId] = key.split(":").map(Number);
        const scheduleIndex = apSchedules.findIndex((item) => item.id === scheduleId);
        const schedule = apSchedules[scheduleIndex];
        const camera = (camerasByScheduleId[scheduleId] || []).find(
          (item) => item.id === cameraId
        );
        const preset = (autoPatrolFlexIgnoreZonesPresetsByCamera[cameraId] || []).find(
          (item) => item.localId === presetLocalId
        );
        if (!schedule || !camera || !preset) {
          return null;
        }
        return {
          schedule: {
            id: schedule.id,
            display_name:
              schedule.title || `Schedule ${scheduleIndex >= 0 ? scheduleIndex + 1 : ""}`,
          },
          camera,
          preset,
          cameraContext: buildCameraContext(camera),
        };
      })
      .filter(Boolean);
  }, [
    autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera,
    apSchedules,
    camerasByScheduleId,
    autoPatrolFlexIgnoreZonesPresetsByCamera,
  ]);

  return (
    <>
      <BackdropLoadingScreen
        openState={submitLoading}
        message="Saving AutoPatrol Flex Ignore Zones..."
        stackAboveModal
      />
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (!submitLoading && reason !== "backdropClick") {
            onClose();
          }
        }}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { fontFamily: "Mulish", minHeight: "85vh" } }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Mulish",
            fontWeight: 700,
            bgcolor: "#1B2A4A",
            color: "white",
            py: 2.25,
            pr: 6,
          }}
        >
          Flex Ignore Zones Onboarding
          <IconButton
            onClick={onClose}
            aria-label="close autopatrol flex ignore zones onboarding dialog"
            disabled={submitLoading}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.12)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {wizardStep === "scheduleSelection" && currentSchedule ? (
            <div>
              <p className="font-mulish text-[20px] font-black">
                {currentSchedule.title || `Schedule ${activeScheduleIndex + 1}`}
              </p>
              <p className="font-mulish text-[16px] pt-[4px] pb-[12px]">
                <span className="font-bold">Cadence:</span>{" "}
                {toCadenceText(currentSchedule)}
              </p>
              <p className="font-mulish text-[14px] pb-[12px] pt-[4px]">
                Select an ignore zones preset for each camera in this schedule.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-[12px] justify-items-start">
                {currentScheduleCameras.map((camera) => {
                  const allPresets =
                    autoPatrolFlexIgnoreZonesPresetsByCamera[camera.id] || [];
                  const selectedLocalId =
                    autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera[
                      `${currentSchedule.id}:${camera.id}`
                    ] || `default-${camera.id}`;
                  const selectablePresets = collapseNoIgnoreZonesPresetOptions(
                    allPresets as any,
                    selectedLocalId
                  );
                  const selectedPreset =
                    selectablePresets.find(
                      (preset) => preset.localId === selectedLocalId
                    ) || selectablePresets[0];
                  if (!selectedPreset) {
                    return null;
                  }
                  return (
                    <FlexIgnoreZonesEditPresetCard
                      key={`${currentSchedule.id}-${camera.id}`}
                      scheduleId={currentSchedule.id}
                      camera={camera as any}
                      preset={selectedPreset as any}
                      cameraContext={
                        buildCameraContext(camera) as any
                      }
                      selectablePresets={selectablePresets as any}
                      selectedPresetLocalId={selectedLocalId}
                      onSelectPreset={(presetLocalId) => {
                        setAutoPatrolFlexIgnoreZonesSelectedPresetForScheduleCamera(
                          currentSchedule.id,
                          camera.id,
                          presetLocalId
                        );
                        setScheduleChangesMade(true);
                      }}
                      onCreateNewPreset={() => {
                        const newLocalId = `ap-custom-${camera.id}-${Date.now()}`;
                        const defaultPresetZones =
                          allPresets.find(
                            (preset) => preset.localId === `default-${camera.id}`
                          )?.zones || [];
                        const nextPreset: AutoPatrolFlexIgnoreZonesPreset = {
                          localId: newLocalId,
                          cameraId: camera.id,
                          name: buildNextPresetName(
                            camera.id,
                            autoPatrolFlexIgnoreZonesPresetsByCamera
                          ),
                          zones: addIgnoreLabel(
                            defaultPresetZones.length > 0
                              ? defaultPresetZones
                              : camera.ignore_zones || []
                          ),
                          persistedId: null,
                          source: "custom",
                          userNamed: false,
                        };
                        setAutoPatrolFlexIgnoreZonesPresetsForCamera(camera.id, [
                          ...allPresets,
                          nextPreset,
                        ]);
                        setAutoPatrolFlexIgnoreZonesSelectedPresetForScheduleCamera(
                          currentSchedule.id,
                          camera.id,
                          nextPreset.localId
                        );
                        setEditorPresetName(nextPreset.name);
                        setNewPresetDialog({
                          scheduleId: currentSchedule.id,
                          cameraId: camera.id,
                          cameraName: camera.camera_name,
                          presetLocalId: nextPreset.localId,
                        });
                        setScheduleChangesMade(true);
                      }}
                      onPreviewImageError={() => {}}
                      showNoIgnoreZonesOption
                    />
                  );
                })}
              </div>
            </div>
          ) : (
            wizardStep === "summary" ? (
              <SummaryStep assignments={assignments as any} />
            ) : (
              <p className="font-mulish text-[14px]">No AutoPatrol schedules available.</p>
            )
          )}
        </DialogContent>
        <DialogActions>
          {(wizardStep === "summary" || activeScheduleIndex > 0) && (
            <OrangeTextButton
              onClick={() => {
                if (wizardStep === "summary") {
                  setAutoPatrolFlexIgnoreZonesActiveScheduleIndexForSite(
                    siteId,
                    Math.max(apSchedules.length - 1, 0)
                  );
                  setWizardStep("scheduleSelection");
                  return;
                }
                setAutoPatrolFlexIgnoreZonesActiveScheduleIndexForSite(
                  siteId,
                  activeScheduleIndex - 1
                );
              }}
              text="Back"
              disabled={submitLoading}
            />
          )}
          <OrangeTextButton
            onClick={() => {
              if (wizardStep === "summary") {
                void handleSubmit();
                return;
              }
              if (activeScheduleIndex < apSchedules.length - 1) {
                setAutoPatrolFlexIgnoreZonesActiveScheduleIndexForSite(
                  siteId,
                  activeScheduleIndex + 1
                );
                return;
              }
              setWizardStep("summary");
            }}
            text={
              wizardStep === "summary"
                ? "Submit"
                : "Next"
            }
            disabled={apSchedules.length === 0 || submitLoading}
          />
        </DialogActions>
      </Dialog>
      <Dialog
        open={newPresetDialog != null}
        onClose={() => setNewPresetDialog(null)}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { fontFamily: "Mulish", maxHeight: "90vh" } }}
      >
        <DialogContent dividers sx={{ overflow: "auto" }}>
          {newPresetDialog && (
            <PresetEditorStep
              cameraName={newPresetDialog.cameraName}
              cameraContext={buildCameraContext(
                (Object.values(camerasByScheduleId)
                  .flat()
                  .find((camera) => camera.id === newPresetDialog.cameraId) as
                  | AutoPatrolFlexCameraSummary
                  | undefined) || {
                  id: newPresetDialog.cameraId,
                  camera_name: newPresetDialog.cameraName,
                  preview_url: liveCameraDataById[newPresetDialog.cameraId]?.preview_url || "",
                  ignore_zones:
                    liveCameraDataById[newPresetDialog.cameraId]?.ignore_zones || [],
                }
              )}
              preset={
                ((autoPatrolFlexIgnoreZonesPresetsByCamera[newPresetDialog.cameraId] ||
                  []).find(
                  (preset) => preset.localId === newPresetDialog.presetLocalId
                ) as any) || {
                  localId: newPresetDialog.presetLocalId,
                  cameraId: newPresetDialog.cameraId,
                  name: editorPresetName,
                  zones: [],
                  persistedId: null,
                  source: "custom",
                  userNamed: false,
                }
              }
              onPresetNameChange={(value) => {
                setEditorPresetName(value);
                const current =
                  autoPatrolFlexIgnoreZonesPresetsByCamera[newPresetDialog.cameraId] || [];
                setAutoPatrolFlexIgnoreZonesPresetsForCamera(
                  newPresetDialog.cameraId,
                  current.map((preset) =>
                    preset.localId === newPresetDialog.presetLocalId
                      ? { ...preset, name: value, userNamed: true }
                      : preset
                  )
                );
              }}
              onPresetZonesChange={(zones) => {
                const current =
                  autoPatrolFlexIgnoreZonesPresetsByCamera[newPresetDialog.cameraId] || [];
                setAutoPatrolFlexIgnoreZonesPresetsForCamera(
                  newPresetDialog.cameraId,
                  current.map((preset) =>
                    preset.localId === newPresetDialog.presetLocalId
                      ? { ...preset, zones: addIgnoreLabel(zones) }
                      : preset
                  )
                );
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <OrangeTextButton
            onClick={() => {
              if (!newPresetDialog) {
                return;
              }
              const current =
                autoPatrolFlexIgnoreZonesPresetsByCamera[newPresetDialog.cameraId] || [];
              setAutoPatrolFlexIgnoreZonesPresetsForCamera(
                newPresetDialog.cameraId,
                current.filter(
                  (preset) =>
                    preset.localId !== newPresetDialog.presetLocalId ||
                    preset.persistedId != null
                )
              );
              setNewPresetDialog(null);
            }}
            text="Cancel"
            disabled={false}
          />
          <OrangeTextButton
            onClick={() => setNewPresetDialog(null)}
            text="Submit"
            disabled={false}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export const AutoPatrolFlexIgnoreZonesScheduleSection = ({
  schedule,
  editMode,
}: {
  schedule: AutoPatrolSchedule;
  editMode: boolean;
}) => {
  const {
    autoPatrolFlexIgnoreZonesPresetsByCamera,
    autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera,
    setAutoPatrolFlexIgnoreZonesSelectedPresetForScheduleCamera,
    setAutoPatrolFlexIgnoreZonesPresetsForCamera,
    setScheduleChangesMade,
  } = useSiteAboutStore();

  const [liveCameraDataById, setLiveCameraDataById] = React.useState<
    Record<number, AutoPatrolLiveCameraData>
  >({});
  const inFlightCameraIdsRef = React.useRef<Set<number>>(new Set());

  const baseCameras = React.useMemo(() => {
    const siteCameraMap = getSiteCameraMapFromStorage();
    return toScheduleCameras(schedule, siteCameraMap);
  }, [schedule]);

  const cameras = React.useMemo(
    () =>
      baseCameras.map((camera) => {
        const live = liveCameraDataById[camera.id];
        return {
          ...camera,
          preview_url: live?.preview_url || camera.preview_url || "",
          ignore_zones: addIgnoreLabel(live?.ignore_zones || camera.ignore_zones || []),
        };
      }),
    [baseCameras, liveCameraDataById]
  );

  React.useEffect(() => {
    const AxiosUI = createApi("");
    const uniqueCameraIds = Array.from(new Set(baseCameras.map((camera) => camera.id)));
    const toFetch = uniqueCameraIds.filter((cameraId) => {
      const alreadyLoaded = liveCameraDataById[cameraId] != null;
      const inFlight = inFlightCameraIdsRef.current.has(cameraId);
      return !alreadyLoaded && !inFlight;
    });
    if (toFetch.length === 0) {
      return;
    }
    toFetch.forEach((cameraId) => inFlightCameraIdsRef.current.add(cameraId));
    void Promise.all(
      toFetch.map(async (cameraId) => {
        try {
          const [statusResponse, zonesResponse] = await Promise.all([
            AxiosUI.get(`/camera/${cameraId}/status/`, { timeout: 8000 }),
            AxiosUI.get(`/camera/${cameraId}/ignore_zones/`, { timeout: 8000 }),
          ]);
          setLiveCameraDataById((previous) => ({
            ...previous,
            [cameraId]: {
              preview_url: String(statusResponse.data?.preview_url || "").trim(),
              ignore_zones: addIgnoreLabel(zonesResponse.data || []),
            },
          }));
        } catch {
          // Keep fallback values from local cache for this camera.
        } finally {
          inFlightCameraIdsRef.current.delete(cameraId);
        }
      })
    );
  }, [schedule.id, baseCameras, liveCameraDataById]);

  React.useEffect(() => {
    cameras.forEach((camera) =>
      ensureCameraPresets(
        camera,
        autoPatrolFlexIgnoreZonesPresetsByCamera,
        setAutoPatrolFlexIgnoreZonesPresetsForCamera
      )
    );
  }, [
    cameras,
    autoPatrolFlexIgnoreZonesPresetsByCamera,
    setAutoPatrolFlexIgnoreZonesPresetsForCamera,
  ]);

  if (cameras.length === 0) {
    return null;
  }

  return (
    <div className="pl-[15px] pr-[20px] pt-[16px] pb-[24px] bg-white">
      <p className="font-mulish text-[18px] font-black pb-[12px]">
        Selected Cameras / Flex Schedule Presets
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-[12px] justify-items-start max-w-[1500px]">
        {cameras.map((camera) => {
          const allPresets = autoPatrolFlexIgnoreZonesPresetsByCamera[camera.id] || [];
          const selectedLocalId =
            autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera[
              `${schedule.id}:${camera.id}`
            ] || `default-${camera.id}`;
          const selectablePresets = collapseNoIgnoreZonesPresetOptions(
            allPresets as any,
            selectedLocalId
          );
          const selectedPreset =
            selectablePresets.find((preset) => preset.localId === selectedLocalId) ||
            selectablePresets[0];
          if (!selectedPreset) {
            return null;
          }
          if (!editMode) {
            return (
              <FlexIgnoreZonesCameraPresetCard
                key={`${schedule.id}-${camera.id}`}
                scheduleId={schedule.id}
                camera={camera as any}
                preset={selectedPreset as any}
                cameraContext={buildCameraContext(camera) as any}
              />
            );
          }
          return (
            <FlexIgnoreZonesEditPresetCard
              key={`${schedule.id}-${camera.id}`}
              scheduleId={schedule.id}
              camera={camera as any}
              preset={selectedPreset as any}
              cameraContext={buildCameraContext(camera) as any}
              selectablePresets={selectablePresets as any}
              selectedPresetLocalId={selectedLocalId}
              onSelectPreset={(presetLocalId) => {
                if (presetLocalId === AP_FLEX_CREATE_NEW_PRESET_OPTION) {
                  return;
                }
                setAutoPatrolFlexIgnoreZonesSelectedPresetForScheduleCamera(
                  schedule.id,
                  camera.id,
                  presetLocalId
                );
                setScheduleChangesMade(true);
              }}
              onCreateNewPreset={() => {
                const defaultPresetZones =
                  allPresets.find((preset) => preset.localId === `default-${camera.id}`)
                    ?.zones || [];
                const newPreset: AutoPatrolFlexIgnoreZonesPreset = {
                  localId: `ap-custom-${camera.id}-${Date.now()}`,
                  cameraId: camera.id,
                  name: buildNextPresetName(
                    camera.id,
                    autoPatrolFlexIgnoreZonesPresetsByCamera
                  ),
                  zones: addIgnoreLabel(
                    defaultPresetZones.length > 0
                      ? defaultPresetZones
                      : camera.ignore_zones || []
                  ),
                  persistedId: null,
                  source: "custom",
                  userNamed: false,
                };
                setAutoPatrolFlexIgnoreZonesPresetsForCamera(camera.id, [
                  ...allPresets,
                  newPreset,
                ]);
                setAutoPatrolFlexIgnoreZonesSelectedPresetForScheduleCamera(
                  schedule.id,
                  camera.id,
                  newPreset.localId
                );
                setScheduleChangesMade(true);
              }}
              onPreviewImageError={() => {}}
              showNoIgnoreZonesOption
            />
          );
        })}
      </div>
    </div>
  );
};

export const AutoPatrolFlexPresetSelectForCameraRow = ({
  siteId,
  scheduleId,
  cameraId,
  cameraName,
  previewUrl,
  defaultZones,
  onZonesChange,
}: {
  siteId: number;
  scheduleId: number;
  cameraId: number;
  cameraName: string;
  previewUrl?: string;
  defaultZones: IgnoreZone[];
  onZonesChange: (zones: IgnoreZone[]) => void;
}) => {
  const {
    autoPatrolFlexIgnoreZonesEnabledBySite,
    autoPatrolFlexIgnoreZonesPresetsByCamera,
    autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera,
    setAutoPatrolFlexIgnoreZonesPresetsForCamera,
  } = useSiteAboutStore();

  const enabled = autoPatrolFlexIgnoreZonesEnabledBySite[siteId] === true;
  const presets = React.useMemo(
    () =>
      autoPatrolFlexIgnoreZonesPresetsByCamera[cameraId] ??
      EMPTY_AUTOPATROL_FLEX_PRESETS,
    [autoPatrolFlexIgnoreZonesPresetsByCamera, cameraId]
  );
  const lastZonesHashRef = React.useRef<string>("");
  const onZonesChangeRef = React.useRef(onZonesChange);
  const [newPresetDialog, setNewPresetDialog] = React.useState<{
    presetLocalId: string;
  } | null>(null);

  React.useEffect(() => {
    onZonesChangeRef.current = onZonesChange;
  }, [onZonesChange]);

  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    if (presets.length === 0) {
      const defaultPreset: AutoPatrolFlexIgnoreZonesPreset = {
        localId: `default-${cameraId}`,
        cameraId,
        name: "Default Preset",
        zones: addIgnoreLabel(defaultZones),
        persistedId: null,
        source: "default",
        userNamed: false,
      };
      const noIgnoreZonesPreset: AutoPatrolFlexIgnoreZonesPreset = {
        localId: AP_FLEX_NO_IGNORE_ZONES_OPTION,
        cameraId,
        name: "No Ignore Zones",
        zones: [],
        persistedId: null,
        source: "custom",
        userNamed: true,
      };
      setAutoPatrolFlexIgnoreZonesPresetsForCamera(cameraId, [
        defaultPreset,
        noIgnoreZonesPreset,
      ]);
    }
  }, [
    enabled,
    presets.length,
    cameraId,
    defaultZones,
    setAutoPatrolFlexIgnoreZonesPresetsForCamera,
  ]);

  const assignedLocalId =
    autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera[
      `${scheduleId}:${cameraId}`
    ] || `default-${cameraId}`;
  const [rowSelectedLocalId, setRowSelectedLocalId] = React.useState("");
  const selectedLocalId = rowSelectedLocalId || assignedLocalId;

  React.useEffect(() => {
    if (!enabled) {
      setRowSelectedLocalId((prev) => (prev === "" ? prev : ""));
      return;
    }
    if (!rowSelectedLocalId) {
      return;
    }
    const stillExists = presets.some((preset) => preset.localId === rowSelectedLocalId);
    if (!stillExists) {
      setRowSelectedLocalId((prev) => (prev === "" ? prev : ""));
    }
  }, [enabled, rowSelectedLocalId, presets]);

  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    const selected = (
      autoPatrolFlexIgnoreZonesPresetsByCamera[cameraId] ??
      EMPTY_AUTOPATROL_FLEX_PRESETS
    ).find((preset) => preset.localId === selectedLocalId);
    const selectedZones = addIgnoreLabel(selected?.zones || []);
    const zonesHash = JSON.stringify(selectedZones);
    if (selected) {
      if (zonesHash === lastZonesHashRef.current) {
        return;
      }
      lastZonesHashRef.current = zonesHash;
      onZonesChangeRef.current(selectedZones);
    }
  }, [
    enabled,
    cameraId,
    selectedLocalId,
    autoPatrolFlexIgnoreZonesPresetsByCamera,
    onZonesChange,
  ]);

  if (!enabled) {
    return null;
  }

  const selectablePresets = collapseNoIgnoreZonesPresetOptions(
    presets as any,
    selectedLocalId
  );
  const hasSelectedPreset = selectablePresets.some(
    (preset) => preset.localId === selectedLocalId
  );
  const selectedValue = hasSelectedPreset ? selectedLocalId : "";

  return (
    <FormControl fullWidth size="small" sx={{ maxWidth: 360, mt: "4px", mb: "8px" }}>
      <InputLabel id={`ap-flex-iz-preset-label-${cameraId}`} sx={{ fontFamily: "Mulish" }}>
        Preset
      </InputLabel>
      <Select
        labelId={`ap-flex-iz-preset-label-${cameraId}`}
        label="Preset"
        value={selectedValue}
        onChange={(event) => {
          const nextValue = String(event.target.value);
          if (nextValue === AP_FLEX_CREATE_NEW_PRESET_OPTION) {
            const newPreset: AutoPatrolFlexIgnoreZonesPreset = {
              localId: `ap-custom-${cameraId}-${Date.now()}`,
              cameraId,
              name: buildNextPresetName(cameraId, autoPatrolFlexIgnoreZonesPresetsByCamera),
              zones: addIgnoreLabel(defaultZones),
              persistedId: null,
              source: "custom",
              userNamed: false,
            };
            setAutoPatrolFlexIgnoreZonesPresetsForCamera(cameraId, [
              ...presets,
              newPreset,
            ]);
            setNewPresetDialog({ presetLocalId: newPreset.localId });
            return;
          }
          setRowSelectedLocalId(nextValue);
        }}
        sx={{ fontFamily: "Mulish" }}
      >
        {!hasSelectedPreset && (
          <MenuItem disabled value="" sx={{ fontFamily: "Mulish" }}>
            Loading presets...
          </MenuItem>
        )}
        {selectablePresets.map((preset) => (
          <MenuItem key={preset.localId} value={preset.localId} sx={{ fontFamily: "Mulish" }}>
            {preset.name}
          </MenuItem>
        ))}
        <MenuItem
          value={AP_FLEX_CREATE_NEW_PRESET_OPTION}
          sx={{ color: "#FF8400", fontFamily: "Mulish", fontWeight: 700 }}
        >
          + CREATE NEW PRESET
        </MenuItem>
      </Select>
      <Dialog
        open={newPresetDialog != null}
        onClose={() => setNewPresetDialog(null)}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { fontFamily: "Mulish", maxHeight: "90vh" } }}
      >
        <DialogContent dividers sx={{ overflow: "auto" }}>
          {newPresetDialog && (
            <PresetEditorStep
              cameraName={cameraName}
              cameraContext={buildCameraContext({
                id: cameraId,
                camera_name: cameraName,
                preview_url: previewUrl || "",
                ignore_zones: addIgnoreLabel(defaultZones),
              })}
              preset={
                ((autoPatrolFlexIgnoreZonesPresetsByCamera[cameraId] || []).find(
                  (preset) => preset.localId === newPresetDialog.presetLocalId
                ) as any) || {
                  localId: newPresetDialog.presetLocalId,
                  cameraId,
                  name: "",
                  zones: addIgnoreLabel(defaultZones),
                  persistedId: null,
                  source: "custom",
                  userNamed: false,
                }
              }
              onPresetNameChange={(value) => {
                const current = autoPatrolFlexIgnoreZonesPresetsByCamera[cameraId] || [];
                setAutoPatrolFlexIgnoreZonesPresetsForCamera(
                  cameraId,
                  current.map((preset) =>
                    preset.localId === newPresetDialog.presetLocalId
                      ? { ...preset, name: value, userNamed: true }
                      : preset
                  )
                );
              }}
              onPresetZonesChange={(zones) => {
                const current = autoPatrolFlexIgnoreZonesPresetsByCamera[cameraId] || [];
                setAutoPatrolFlexIgnoreZonesPresetsForCamera(
                  cameraId,
                  current.map((preset) =>
                    preset.localId === newPresetDialog.presetLocalId
                      ? { ...preset, zones: addIgnoreLabel(zones) }
                      : preset
                  )
                );
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <OrangeTextButton
            onClick={() => {
              if (!newPresetDialog) {
                return;
              }
              const current = autoPatrolFlexIgnoreZonesPresetsByCamera[cameraId] || [];
              setAutoPatrolFlexIgnoreZonesPresetsForCamera(
                cameraId,
                current.filter(
                  (preset) =>
                    preset.localId !== newPresetDialog.presetLocalId ||
                    preset.persistedId != null
                )
              );
              setNewPresetDialog(null);
            }}
            text="Cancel"
            disabled={false}
          />
          <OrangeTextButton
            onClick={() => {
              if (!newPresetDialog) {
                return;
              }
              setRowSelectedLocalId(newPresetDialog.presetLocalId);
              setNewPresetDialog(null);
            }}
            text="Submit"
            disabled={false}
          />
        </DialogActions>
      </Dialog>
    </FormControl>
  );
};

export const AutoPatrolFlexIgnoreZonesOffboardingDialog = ({
  open,
  siteId,
  schedules,
  onClose,
  onSubmitted,
}: {
  open: boolean;
  siteId: number;
  schedules: AutoPatrolSchedule[];
  onClose: () => void;
  onSubmitted: () => void;
}) => {
  const {
    autoPatrolFlexIgnoreZonesPresetsByCamera,
    autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera,
    setAutoPatrolFlexIgnoreZonesPresetsForCamera,
  } = useSiteAboutStore();
  const siteCameraMap = React.useMemo(() => getSiteCameraMapFromStorage(), []);
  const cameras = React.useMemo(() => {
    const byId = new Map<number, AutoPatrolFlexCameraSummary>();
    schedules.forEach((schedule) => {
      toScheduleCameras(schedule, siteCameraMap).forEach((camera) => {
        if (!byId.has(camera.id)) {
          byId.set(camera.id, camera);
        }
      });
    });
    return Array.from(byId.values());
  }, [schedules, siteCameraMap]);

  const [liveCameraDataById, setLiveCameraDataById] = React.useState<
    Record<number, AutoPatrolLiveCameraData>
  >({});
  const [choiceByCameraId, setChoiceByCameraId] = React.useState<Record<number, string>>({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const inFlightCameraIdsRef = React.useRef<Set<number>>(new Set());

  const enrichedCameras = React.useMemo(
    () =>
      cameras.map((camera) => {
        const live = liveCameraDataById[camera.id];
        return {
          ...camera,
          preview_url: live?.preview_url || camera.preview_url || "",
          ignore_zones: addIgnoreLabel(live?.ignore_zones || camera.ignore_zones || []),
        };
      }),
    [cameras, liveCameraDataById]
  );

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setError(null);
    setLoading(false);
    setChoiceByCameraId({});
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const AxiosUI = createApi("");
    const cameraIds = cameras.map((camera) => camera.id);
    const toFetch = cameraIds.filter((cameraId) => {
      const alreadyLoaded = liveCameraDataById[cameraId] != null;
      const inFlight = inFlightCameraIdsRef.current.has(cameraId);
      return !alreadyLoaded && !inFlight;
    });
    if (toFetch.length === 0) {
      return;
    }
    toFetch.forEach((cameraId) => inFlightCameraIdsRef.current.add(cameraId));
    void Promise.all(
      toFetch.map(async (cameraId) => {
        const baseCamera = cameras.find((camera) => camera.id === cameraId);
        try {
          const [statusResponse, zonesResponse] = await Promise.all([
            AxiosUI.get(`/camera/${cameraId}/status/`, { timeout: 8000 }),
            AxiosUI.get(`/camera/${cameraId}/ignore_zones/`, { timeout: 8000 }),
          ]);
          const preview_url = statusResponse?.data?.preview_url || baseCamera?.preview_url || "";
          const ignore_zones = addIgnoreLabel(parseListResponse(zonesResponse.data));
          setLiveCameraDataById((previous) => ({
            ...previous,
            [cameraId]: { preview_url, ignore_zones },
          }));
        } catch {
          setLiveCameraDataById((previous) => ({
            ...previous,
            [cameraId]: {
              preview_url: baseCamera?.preview_url || "",
              ignore_zones: addIgnoreLabel(baseCamera?.ignore_zones || []),
            },
          }));
        } finally {
          inFlightCameraIdsRef.current.delete(cameraId);
        }
      })
    );
  }, [open, cameras, liveCameraDataById]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    enrichedCameras.forEach((camera) => {
      ensureCameraPresets(
        camera,
        autoPatrolFlexIgnoreZonesPresetsByCamera,
        setAutoPatrolFlexIgnoreZonesPresetsForCamera
      );
    });
  }, [
    open,
    enrichedCameras,
    autoPatrolFlexIgnoreZonesPresetsByCamera,
    setAutoPatrolFlexIgnoreZonesPresetsForCamera,
  ]);

  React.useEffect(() => {
    if (!open || cameras.length === 0) {
      return;
    }
    setChoiceByCameraId((previous) => {
      const next = { ...previous };
      cameras.forEach((camera) => {
        if (next[camera.id]) {
          return;
        }
        const selectedFromAnySchedule = Object.entries(
          autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera
        ).find(([key, presetLocalId]) => key.endsWith(`:${camera.id}`) && !!presetLocalId)?.[1];
        const presets = collapseNoIgnoreZonesPresetOptions(
          autoPatrolFlexIgnoreZonesPresetsByCamera[camera.id] || [],
          selectedFromAnySchedule || undefined
        );
        next[camera.id] = selectedFromAnySchedule || presets[0]?.localId || AP_FLEX_NO_IGNORE_ZONES_OPTION;
      });
      return next;
    });
  }, [
    open,
    cameras,
    autoPatrolFlexIgnoreZonesSelectedPresetByScheduleCamera,
    autoPatrolFlexIgnoreZonesPresetsByCamera,
  ]);

  const selectionComplete = cameras.every((camera) => Boolean(choiceByCameraId[camera.id]));

  const handleSubmit = async () => {
    if (!selectionComplete) {
      alert("Please make a choice for every camera before submitting.");
      return;
    }
    setLoading(true);
    setError(null);
    const AxiosUI = createApi("");
    try {
      for (const camera of enrichedCameras) {
        const selectedLocalId = choiceByCameraId[camera.id];
        if (selectedLocalId === AP_FLEX_NO_IGNORE_ZONES_OPTION) {
          const payload = await buildNoIgnoreZonesPostPayload(
            camera as any,
            addIgnoreLabel(camera.ignore_zones || []),
            AxiosUI as any
          );
          await AxiosUI.post(`/camera/${camera.id}/ignore_zones/`, payload);
          continue;
        }
        const selectedPreset = (autoPatrolFlexIgnoreZonesPresetsByCamera[camera.id] || []).find(
          (preset) => preset.localId === selectedLocalId
        );
        let zonesToApply = addIgnoreLabel(selectedPreset?.zones || []);
        if (
          zonesToApply.length === 0 &&
          selectedPreset?.persistedId != null &&
          Number.isFinite(Number(selectedPreset.persistedId))
        ) {
          try {
            const presetZonesResponse = await AxiosUI.get(
              `preset-zone/?camera=${camera.id}&preset=${selectedPreset.persistedId}`
            );
            zonesToApply = addIgnoreLabel(
              toMappedPresetZones(
                selectedPreset?.name || `Preset ${selectedPreset?.persistedId}`,
                parseListResponse(presetZonesResponse.data),
                addIgnoreLabel(camera.ignore_zones || [])
              )
            );
          } catch {
            zonesToApply = addIgnoreLabel(selectedPreset?.zones || []);
          }
        }
        const payload = zonesToApply.map((zone) => ({
          zone_type: zone.zone_type,
          stream_id: zone.stream_id,
          stream_name: ignoreZoneStreamNameForCameraApi(zone as any),
          metric_id: zone.metric_id,
          label: zone.label,
          label_display_name: zone.label_display_name,
          line_color: zone.line_color,
          coordinates: replaceNegativeWithZero(
            ((zone.coordinates || []).filter((arr: unknown) => {
              return Array.isArray(arr) && arr.length > 0;
            }) as unknown as number[][][])
          ),
        }));
        await AxiosUI.post(`/camera/${camera.id}/ignore_zones/`, payload);
      }

      for (const schedule of schedules) {
        try {
          const response = await AxiosUI.get(
            `auto-patrol-schedule-preset/?auto_patrol_schedule=${schedule.id}`
          );
          const rows = parseListResponse(response.data);
          for (const row of rows) {
            const assignmentId = toNumericId((row as any)?.id);
            if (assignmentId != null) {
              await AxiosUI.delete(`auto-patrol-schedule-preset/${assignmentId}/`);
            }
          }
        } catch {
          // Best effort cleanup; continue with remaining schedules.
        }
      }

      await deletePersistedFlexPresetRecordsAfterIgnoreZonesSave(
        AxiosUI as any,
        autoPatrolFlexIgnoreZonesPresetsByCamera as any,
        enrichedCameras.map((camera) => ({ id: camera.id })),
        async () => {}
      );

      setLoading(false);
      onSubmitted();
      onClose();
    } catch (submitError: any) {
      setLoading(false);
      setError(submitError?.message || "Failed to complete AutoPatrol Flex Ignore Zones offboarding.");
    }
  };

  return (
    <>
      <BackdropLoadingScreen
        openState={loading}
        message="Applying camera ignore zones selections..."
        stackAboveModal
      />
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (!loading && reason !== "backdropClick") {
            onClose();
          }
        }}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            fontFamily: "Mulish",
            minHeight: "80vh",
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: "Mulish", fontWeight: 700 }}>
          AutoPatrol Flex Ignore Zones Offboarding
        </DialogTitle>
        <DialogContent dividers>
          <p className="font-mulish text-[14px] pb-[12px]">
            Choose what each camera should keep after turning Flex Ignore Zones off.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-[12px] justify-items-start max-w-[1500px]">
            {enrichedCameras.map((camera) => {
              const selectedLocalId =
                choiceByCameraId[camera.id] || AP_FLEX_NO_IGNORE_ZONES_OPTION;
              const availablePresets = collapseNoIgnoreZonesPresetOptions(
                autoPatrolFlexIgnoreZonesPresetsByCamera[camera.id] || [],
                selectedLocalId
              );
              const selectedPreset =
                availablePresets.find((preset) => preset.localId === selectedLocalId) || {
                  localId: AP_FLEX_NO_IGNORE_ZONES_OPTION,
                  cameraId: camera.id,
                  name: "No Ignore Zones",
                  zones: [],
                  persistedId: null,
                  source: "custom",
                  userNamed: true,
                };

              return (
                <FlexIgnoreZonesCameraPresetCard
                  key={`ap-offboarding-${camera.id}`}
                  scheduleId={siteId}
                  camera={camera as any}
                  preset={selectedPreset as any}
                  cameraContext={buildCameraContext(camera) as any}
                  presetLineReplacement={
                    <FormControl size="small" fullWidth>
                      <InputLabel id={`ap-offboarding-select-${camera.id}`}>Preset</InputLabel>
                      <Select
                        labelId={`ap-offboarding-select-${camera.id}`}
                        value={selectedLocalId}
                        label="Preset"
                        sx={{ fontFamily: "Mulish", minWidth: 220 }}
                        onChange={(event) => {
                          setChoiceByCameraId((previous) => ({
                            ...previous,
                            [camera.id]: String(event.target.value),
                          }));
                        }}
                      >
                        {availablePresets.map((preset) => (
                          <MenuItem
                            key={preset.localId}
                            value={preset.localId}
                            sx={{ fontFamily: "Mulish" }}
                          >
                            {preset.name}
                          </MenuItem>
                        ))}
                        <MenuItem
                          value={AP_FLEX_NO_IGNORE_ZONES_OPTION}
                          sx={{ fontFamily: "Mulish" }}
                        >
                          No Ignore Zones
                        </MenuItem>
                      </Select>
                    </FormControl>
                  }
                />
              );
            })}
          </div>
          {error && <p className="font-mulish text-[13px] text-red-600 mt-3">{error}</p>}
        </DialogContent>
        <DialogActions>
          <OrangeTextButton onClick={onClose} text="Cancel" disabled={loading} />
          <OrangeTextButton
            onClick={() => void handleSubmit()}
            text="Submit"
            disabled={!selectionComplete || loading}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

